import { defineStore } from "pinia";
import turndownService from "turndown";
import {
    IAsset,
    IAssetDetails,
    IMessagePlaceholder,
    INewMessage,
    ISendMessageQuery
} from "../../../resources-vue/vue-interfaces/messaging-service/i-message";
import { useConversationStore } from "./useConversationStore";
import { getFileIcon } from "../../../common/vue-helpers/modifiers/chat-modifiers";
import { MessageService } from "../../../services/message-service";
import { Container } from "aurelia-dependency-injection";
import { useApp } from "../../App/useApp";
import { formatTaggedUserMessage } from "../../../common/vue-helpers/modifiers/format-message";
import { useMessagesStore } from "./useMessagesStore";
import { useDraftsStore } from "./useDraftsStore";
import { PlaceholdersListEnum } from "../../../enums/PlaceholdersListEnum";
import { useUserStore } from "./useUsersStore";
import { DraftConvEnum } from "../../../enums/DraftConvEnums";

interface IFileOption {
    fileName: string;
    iconClass: string;
}

interface IEditorMessage {
    text: string;
    files: File[];
    assetIds: any;
    placeHoldersList: IMessagePlaceholder[];
    allSelectedProfiles: any[];
    isImportant: boolean;
    replyMessage: any;
}

export const useEditorStore = defineStore("editor", {
    state() {
        return {
            editorMessage: {
                text: null,
                files: [],
                assetIds: [],
                placeHoldersList: [],
                allSelectedProfiles: [],
                isImportant: false,
                replyMessage: null
            } as IEditorMessage,

            newChatTitle: "" as string,
            newChatUsers: [] as any[]
        };
    },

    getters: {
        getFilesUI: (state) => {
            let filesUI: any = [];

            if (state.editorMessage.files?.length) {
                for (const file of state.editorMessage.files) {
                    let fileOption: IFileOption = {
                        fileName: "",
                        iconClass: "fa-file"
                    };
                    fileOption.iconClass = getFileIcon(file?.type);
                    fileOption.fileName = file.name;
                    filesUI.push(fileOption);
                }
            }
            return filesUI;
        },

        canSend: (state) => {
            return !!(
                state.editorMessage.text.trim() ||
                state.editorMessage.assetIds.length ||
                state.editorMessage.placeHoldersList.length
            );
        },

        disableEditor() {
            const convStore = useConversationStore();
            return convStore.getActiveConversation?.hasUserLeftChat;
        },

        isMsgEmpty: (state) => {
            const _turndownService = new turndownService();
            const prevTextMsg = state.editorMessage.text ? _turndownService.turndown(state.editorMessage.text) : "";
            return !(
                state.editorMessage.allSelectedProfiles.length ||
                state.editorMessage.assetIds.length ||
                state.editorMessage.files.length ||
                state.editorMessage.placeHoldersList.length ||
                prevTextMsg !== ""
            );
        }
    },

    actions: {
        setEditorMessage(payload) {
            this.editorMessage = payload;
        },

        setNewChatDetails(title: string, users: any[]) {
            this.newChatTitle = title;
            this.newChatUsers = users;
        },

        removeFile(fileName: string) {
            this.editorMessage.files = this.editorMessage.files.filter((file) => file.name !== fileName);
        },

        removeProfile(removedProfile: any) {
            const id = removedProfile.hasOwnProperty("id") ? removedProfile.id : removedProfile.patientId;
            this.editorMessage.allSelectedProfiles = this.editorMessage.allSelectedProfiles.filter(
                (profile: any) => (profile.hasOwnProperty("id") ? profile.id : profile.patientId) !== id
            );
        },

        async formatAndSendMessage(convId: string, isNewConversation: boolean = false) {
            let assets: IAsset[] = [];
            let assetDetails: IAssetDetails[] = [];
            let isError: boolean = false;

            const messageService = Container.instance.get(MessageService);
            const appStore = useApp();
            const msgStore = useMessagesStore();
            const convStore = useConversationStore();

            // upload message assets
            if (this.editorMessage.files.length) {
                if (this.editorMessage.files?.length > 25) {
                    this.editorMessage.files.splice(25);
                }
                for (const file of this.editorMessage.files) {
                    assets.push({
                        fileName: "",
                        url: "",
                        mimeType: "",
                        file: file
                    });
                }
                try {
                    let files = assets.map((asset) => asset.file);
                    if (!!files?.length) {
                        assetDetails = await messageService.uploadMessageFiles(files as unknown as File[]);
                    }
                } catch (e) {
                    console.error(e);
                    isError = true;
                }
            }
            if (!isError) {
                const draftStore = useDraftsStore();
                let allPlaceholders = [];
                let formattedMsg = "";

                if (this.editorMessage.allSelectedProfiles.length) {
                    const profilePlaceholders = this.getProfilePlaceholdersList();
                    allPlaceholders = [...profilePlaceholders];
                }

                if (!!this.editorMessage.text && this.editorMessage.text.length > 0) {
                    const { msg, taggedUsersList } = this.replaceTaggedUsers(this.editorMessage.text);
                    let htmlMarkdown = new turndownService().turndown(msg);
                    let message = htmlMarkdown.replace(/%{TAGGEDUSER}%/g, "%{TAGGED_USER}% ");
                    allPlaceholders = [...allPlaceholders, ...taggedUsersList];
                    if (!!message && message.length > 0) {
                        formattedMsg = message;
                    }
                }

                if (convStore.getActiveConversation?.isDraftConversation) {
                    const userStore = useUserStore();
                    const userIds = userStore
                        .getMultipleUsers(convStore.getActiveConversation.loginIds)
                        .map((user) => user.userId);
                    const messageI: INewMessage = {
                        title: convStore.getActiveConversation.title,
                        userIds,
                        message: formattedMsg,
                        isImportant: this.editorMessage.isImportant,
                        assets: assetDetails,
                        senderDisplayFirstname: appStore.loggedInUserDetails.firstName,
                        senderDisplayLastname: appStore.loggedInUserDetails.lastName,
                        placeHoldersList: allPlaceholders
                    };
                    let conversation = await messageService.newConversation(messageI);
                    draftStore.updateDraftConversation({
                        id: convStore.getActiveConversation.id,
                        actionType: DraftConvEnum.CreateConv
                    });
                    convStore.setActiveConversationId(conversation.id);
                    await convStore.fetchConversationById(conversation.id);
                } else if (isNewConversation) {
                    const userIds = this.newChatUsers.map((user) => user.userId);
                    const payload: INewMessage = {
                        title: this.newChatTitle,
                        userIds,
                        message: formattedMsg,
                        isImportant: this.editorMessage.isImportant,
                        assets: assetDetails,
                        senderDisplayFirstname: appStore.loggedInUserDetails.firstName,
                        senderDisplayLastname: appStore.loggedInUserDetails.lastName,
                        placeHoldersList: []
                    };
                    let conversation = await messageService.newConversation(payload);
                    this.resetEditorMessage();

                    const newConversation = await messageService.getConversationById(conversation.id);
                    convStore.setActiveConversationId(conversation.id);

                    convStore.addNewConversation(newConversation.conversation);
                } else {
                    let messageQuery: ISendMessageQuery = {
                        message: formattedMsg,
                        isImportant: this.editorMessage.isImportant,
                        assets: assetDetails,
                        senderDisplayFirstname: appStore.loggedInUserDetails.firstName,
                        senderDisplayLastname: appStore.loggedInUserDetails.lastName,
                        replyToMessageId: this.editorMessage.replyMessage ? this.editorMessage.replyMessage.id : null,
                        placeHoldersList: allPlaceholders
                    };

                    const tempId = `temp-${crypto.randomUUID()}`;

                    const cacheMsg = {
                        id: tempId,
                        text: formattedMsg,
                        isRead: true,
                        isImportant: this.editorMessage.isImportant,
                        isFromSelf: true,
                        isEdited: false,
                        createdOn: new Date().toISOString().slice(0, -1),
                        modifiedOn: new Date().toISOString().slice(0, -1),
                        user: null,
                        isLoading: true,
                        isError: false,
                        assetIds: [],
                        assets,
                        senderDetails: {
                            firstName: appStore.loggedInUserDetails.firstName,
                            lastName: appStore.loggedInUserDetails.lastName,
                            email: appStore.loggedInUserDetails.email
                        },
                        replyMessage: this.editorMessage.replyMessage
                            ? await this.fetchMessageFromId(this.editorMessage.replyMessage.id, convId)
                            : null,
                        placeHoldersList: allPlaceholders,
                        conversationId: convId,
                        isDeprecated: false,
                        deliveryStatus: "sending"
                    };
                    msgStore.sendMessage(convId, messageQuery, cacheMsg);
                    draftStore.removeDraftMessage(convId);
                }

                // Reset Editor
                this.resetEditorMessage();
            }
        },

        getProfilePlaceholdersList() {
            return this.editorMessage.allSelectedProfiles.map((profile: any) => {
                if (profile?.name) {
                    // when user is editing a message
                    return {
                        type: profile.type,
                        id: profile.id,
                        agencyId: profile.agencyId,
                        applicationId: profile.application,
                        agencyName: profile.agencyName,
                        name: profile.name
                    };
                } else {
                    const isEmployeeProfile = profile.hasOwnProperty("id");
                    return {
                        type: isEmployeeProfile ? PlaceholdersListEnum.ShareUser : PlaceholdersListEnum.SharePatient,
                        id: isEmployeeProfile ? profile.sourceUserId : profile.patientId,
                        agencyId: profile.agencyId,
                        applicationId: profile.application,
                        agencyName: profile.agencyName,
                        name: profile.firstName + " " + profile.lastName
                    };
                }
            });
        },

        replaceTaggedUsers(msg: string) {
            const convStore = useConversationStore();
            const userStore = useUserStore();
            const list = [];

            const parser = new DOMParser();
            const html = parser.parseFromString(msg, "text/html").body;
            let elements = html.querySelectorAll('[data-actiontype="tagged-user"]');
            if (elements.length > 0) {
                elements.forEach((ele) => {
                    const users = userStore.getMultipleUsers(convStore.getActiveConversation.loginIds);
                    const details = users.find((user) => user.loginId === ele.id);
                    list.push({
                        type: PlaceholdersListEnum.TaggedUser,
                        id: details.loginId,
                        name: details.firstName + " " + details.lastName,
                        applicationId: 0, // optional field for tagged user, hence sending 0
                        agencyId: "122ec20a-705b-4974-9437-8f6233a6e953", // optional field, sending random agencyID till the backend is fixed.
                        agencyName: ""
                    });
                    ele.replaceWith(`%{TAGGEDUSER}%`);
                });
                return { msg: html.outerHTML, taggedUsersList: list };
            }
            return { msg, taggedUsersList: list };
        },

        async fetchMessageFromId(messageId: string, convId: string) {
            const messageService = Container.instance.get(MessageService);
            const msgStore = useMessagesStore();
            const convMessages = msgStore.getConversationMessages;
            const fetchedMessage = convMessages.find((message) => message.id === messageId);
            if (fetchedMessage) {
                const { text } = formatTaggedUserMessage(fetchedMessage.text, fetchedMessage.placeHoldersList, "");
                return {
                    assetIds: fetchedMessage.assetIds,
                    createdOn: fetchedMessage.createdOn,
                    isImportant: fetchedMessage.isImportant,
                    messageId: fetchedMessage.id,
                    senderDetails: fetchedMessage.senderDetails ? fetchedMessage.senderDetails : fetchedMessage.user,
                    senderUserId: fetchedMessage.senderUserId,
                    text: text,
                    placeHoldersList: fetchedMessage.placeHoldersList
                };
            } else {
                let messagesResult = (await messageService.getMessageById(convId, messageId)).messageData;
                const { text } = formatTaggedUserMessage(messagesResult.text, messagesResult.placeHoldersList, "");
                return {
                    assetIds: messagesResult.assetIds,
                    createdOn: messagesResult.createdOn,
                    isImportant: messagesResult.isImportant,
                    messageId: messagesResult.id,
                    senderDetails: messagesResult.senderDetails,
                    senderUserId: messagesResult.senderUserId,
                    text: text,
                    placeHoldersList: messagesResult.placeHoldersList
                };
            }
        },

        resetEditorMessage() {
            this.editorMessage = {
                text: "",
                files: [],
                assetIds: [],
                placeHoldersList: [],
                allSelectedProfiles: [],
                isImportant: false,
                replyMessage: null
            };

            this.newChatTitle = "";
            this.newChatUsers = [];
        }
    }
});
